import { Link } from "react-router-dom";
import {
  SITE_PAGE_SHELL_CLASS,
  SitePageAtmosphere,
  SITE_PAGE_BODY_PADDING_TOP,
} from "./SitePageChrome.jsx";

/* Broken-state mirror of the hero billboard HUD: same frame, corner brackets,
 * and telemetry rails, but every readout reports failure. */

const CYAN = "#97f2f8";
const PINK = "#f08fb4";
const GOLD = "#c9973a";

const CornerBracket = ({ position }) => {
  const edges = {
    "top-left": "-top-1 -left-1 border-t-4 border-l-4",
    "top-right": "-top-1 -right-1 border-t-4 border-r-4",
    "bottom-left": "-bottom-1 -left-1 border-b-4 border-l-4",
    "bottom-right": "-bottom-1 -right-1 border-b-4 border-r-4",
  };
  return (
    <span
      aria-hidden
      className={`absolute w-7 h-7 border-[#f08fb4] ${edges[position]}`}
    />
  );
};

/* Suspension span that runs out of deck mid-air: solid gold on the left,
 * a snapped cable curling down at the break, a dashed run fading out,
 * and a ghosted far tower that was never reached. */
const SeveredBridge = () => (
  <svg
    viewBox="0 0 900 210"
    className="w-full max-w-3xl mx-auto mt-12 sm:mt-16"
    aria-hidden
    role="presentation"
  >
    {/* ghost far side - desaturated, unreachable */}
    <g stroke="#3d4a80" fill="none" opacity="0.55">
      <line x1="690" y1="172" x2="900" y2="172" strokeWidth="4" />
      <line x1="790" y1="42" x2="790" y2="188" strokeWidth="7" />
      <path d="M900 60 C 860 66, 812 44, 790 44" strokeWidth="2.5" />
      <path d="M790 44 C 762 44, 716 110, 700 168" strokeWidth="2.5" strokeDasharray="7 9" />
      <line x1="820" y1="58" x2="820" y2="172" strokeWidth="1.5" />
      <line x1="850" y1="54" x2="850" y2="172" strokeWidth="1.5" />
      <line x1="880" y1="52" x2="880" y2="172" strokeWidth="1.5" />
    </g>

    {/* solid gold near side */}
    <g stroke={GOLD} fill="none">
      {/* deck, ending abruptly past the tower */}
      <line x1="0" y1="172" x2="432" y2="172" strokeWidth="6" />
      {/* tower */}
      <line x1="298" y1="30" x2="298" y2="192" strokeWidth="9" />
      <line x1="278" y1="60" x2="318" y2="60" strokeWidth="4" />
      {/* main cable up to the tower top */}
      <path d="M0 96 C 110 158, 210 62, 296 38" strokeWidth="3.5" />
      {/* hangers */}
      <line x1="45" y1="118" x2="45" y2="172" strokeWidth="2" />
      <line x1="95" y1="132" x2="95" y2="172" strokeWidth="2" />
      <line x1="145" y1="126" x2="145" y2="172" strokeWidth="2" />
      <line x1="195" y1="99" x2="195" y2="172" strokeWidth="2" />
      <line x1="245" y1="66" x2="245" y2="172" strokeWidth="2" />
      {/* snapped cable curling downward past the break */}
      <path d="M300 38 C 356 52, 402 106, 424 148 C 432 163, 424 172, 412 166" strokeWidth="3" />
    </g>

    {/* dashed run fading into nothing */}
    <g stroke={GOLD} fill="none" strokeWidth="6" strokeDasharray="26 20">
      <line x1="448" y1="172" x2="530" y2="172" opacity="0.55" />
      <line x1="530" y1="172" x2="600" y2="172" opacity="0.3" />
      <line x1="600" y1="172" x2="656" y2="172" opacity="0.12" />
    </g>
  </svg>
);

const NotFoundPage = () => {
  return (
    <div
      className={`${SITE_PAGE_SHELL_CLASS} flex flex-col justify-center items-center px-4 sm:px-6 pb-16 ${SITE_PAGE_BODY_PADDING_TOP}`}
    >
      <SitePageAtmosphere />

      <div className="relative z-10 w-full max-w-5xl">
        {/* HUD frame - same chassis as the hero billboard, failure state */}
        <div className="relative border border-[#7fd4ee]/40 bg-[#101538]/60">
          <CornerBracket position="top-left" />
          <CornerBracket position="top-right" />
          <CornerBracket position="bottom-left" />
          <CornerBracket position="bottom-right" />

          {/* status rail */}
          <div className="flex justify-between items-center px-5 sm:px-8 pt-5 font-mono text-xs sm:text-sm tracking-[0.25em]">
            <span className="flex items-center gap-2.5 text-[#f08fb4]">
              <span className="nf-pulse-dot w-2 h-2 rounded-full bg-[#f08fb4]" aria-hidden />
              SIGNAL LOST
            </span>
            <span className="text-[#97f2f8]">SYS.ERR</span>
          </div>

          <div className="px-5 sm:px-8 py-10 sm:py-14 text-center">
            <div className="relative inline-block nf-glitch">
              <h1 className="font-mono font-bold text-8xl sm:text-9xl md:text-[10rem] leading-none text-[#97f2f8] tracking-[0.08em]">
                404
              </h1>
              <div
                aria-hidden
                className="nf-scanlines absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(to bottom, transparent 0px, transparent 3px, rgba(11,14,52,0.35) 3px, rgba(11,14,52,0.35) 4px)",
                }}
              />
            </div>

            <h2 className="mt-6 text-2xl sm:text-3xl font-bold text-white tracking-tight">
              This bridge leads to Nowhere
            </h2>

            <p className="mt-4 max-w-xl mx-auto text-gray-300 text-sm sm:text-base leading-relaxed">
              Pittsburgh has 446 bridges, but none of them lead here. The page
              you're looking for was torn down or never built.
            </p>
          </div>

          {/* telemetry rail */}
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-between sm:items-center px-5 sm:px-8 pb-5 font-mono text-xs sm:text-sm tracking-[0.2em] text-[#97f2f8]/80">
            <span className="whitespace-nowrap">LAT --.---- LON --.----</span>
            <span className="whitespace-nowrap">ROUTE UNRESOLVED 000%</span>
          </div>
        </div>

        {/* CTAs - one clear primary */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-10">
          <Link
            to="/"
            className="w-full sm:w-auto text-center px-8 py-3 bg-gradient-to-r from-cyan-400 to-pink-400 text-gray-900 font-bold rounded-full shadow-lg hover:shadow-cyan-400/20 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
          >
            Back to Home
          </Link>
          <Link
            to="/schedule"
            className="hidden sm:block sm:w-auto text-center px-8 py-3 border border-white/25 text-white font-bold rounded-full hover:border-cyan-400 hover:bg-cyan-400/10 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
          >
            View Schedule
          </Link>
        </div>

        <SeveredBridge />
      </div>

      <style>{`
        @keyframes nf-glitch {
          0%, 92.4%, 95.4%, 100% { transform: translate(0, 0); text-shadow: none; }
          93% { transform: translate(-3px, 1px); text-shadow: 3px 0 ${PINK}, -3px 0 ${CYAN}; }
          94.2% { transform: translate(3px, -1px); text-shadow: -3px 0 ${PINK}, 3px 0 ${CYAN}; }
        }
        .nf-glitch h1 {
          animation: nf-glitch 6s steps(1, end) infinite;
        }
        @keyframes nf-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.25; }
        }
        .nf-pulse-dot {
          animation: nf-pulse 2.4s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .nf-glitch h1, .nf-pulse-dot { animation: none; }
          .nf-scanlines { display: none; }
        }
      `}</style>
    </div>
  );
};

export default NotFoundPage;
